import type { GridRowModel } from '@mui/x-data-grid-premium';

import type { IVersionsWorkerState } from '../../../states/versionsWorker/versionsWorker.state';

/**
 * WA-3633 — produced-quantity commit contract for the screen-level
 * processRowUpdate. Returns the versionsWorkerState update to publish when the
 * produced quantity changed, or null when it did not (so the caller can skip
 * the publish and the "unsaved changes" flag).
 *
 * The produced-articles grid `data` prop comes from versionsWorkerState, whose
 * sub layer REBUILDS the producedArticles array on every publish (sort +
 * search) while preserving element references. So the committed value is
 * written by mutating the matching produced-article element IN PLACE — that is
 * what makes the republished, rebuilt array carry the new value instead of
 * reverting it (the 3540a1c5 lesson). The array reference itself is passed
 * through unchanged. recordsToSave is NOT bound to the grid rows prop (read
 * only by the Speichern mutation), so it is safely remapped into a fresh array
 * by recordId.
 */
export const commitProducedQuantity = (
  newRow: GridRowModel,
  oldRow: GridRowModel,
  versions: IVersionsWorkerState,
): IVersionsWorkerState | null => {
  if (newRow.producedQuantity === oldRow.producedQuantity) {
    return null;
  }

  const { recordId } = newRow;
  const target = versions.producedArticles.find((article) => article.recordId === recordId);
  if (target) {
    target.producedQuantity = newRow.producedQuantity;
  }

  return {
    ...versions,
    producedArticles: versions.producedArticles,
    recordsToSave: versions.recordsToSave.map((article) =>
      article.recordId === recordId
        ? { ...article, producedQuantity: newRow.producedQuantity }
        : article,
    ),
  };
};
